import {Tooltip} from '@material-ui/core';
import {Button, ButtonVariant} from '@wandb/weave/components/Button';
import React, {useContext} from 'react';

import {LoginPopup} from '../../../components/LoginPopup';
import {
  ReportDiscussionContext,
  ReportDiscussionUpdaterContext,
} from '../../../components/ReportDiscussion/ReportDiscussionContext';
import {ShareReportTrigger} from '../../../components/ShareReportTrigger/ShareReportTrigger';
import {useReportStar} from '../hooks';
import {useLoadedReportContext} from '../LoadedReportContext';
import {useReportPageContext} from '../ReportPageContext';
import * as S from '../ReportPageHeader/ReportPageHeader.styles';
import {ReportPageMenuActions} from '../ReportPageHeader/ReportPageMenuActions';

type ReportPageViewHeaderProps = {
  viewerOwnsReport: boolean;
  isFloatingBar?: boolean;
  shareReportVariant?: ButtonVariant;
  loginPopupOffset?: number;
};

interface ConditionalWrapperProps {
  condition: boolean;
  children?: React.ReactNode;
}

const ConditionalWrapper = ({
  condition,
  children,
}: ConditionalWrapperProps): JSX.Element => {
  return condition ? (
    <S.ReportPageHeader>{children}</S.ReportPageHeader>
  ) : (
    <>{children}</>
  );
};

export const ReportPageViewHeader = ({
  viewerOwnsReport,
  isFloatingBar,
  shareReportVariant,
  loginPopupOffset,
}: ReportPageViewHeaderProps) => {
  const {
    isAdmin,
    viewer,
    rootReportId,
    isPublicProject,
    teamMemberInfo: {viewerIsTeamAdmin, viewerIsTeamMember},
  } = useReportPageContext();
  const {reportViewRef, view} = useLoadedReportContext();

  const isLoggedIn = viewer !== undefined && viewer !== null;

  const {
    starCount = 0,
    starred,
    toggleStar,
  } = useReportStar({
    reportId: rootReportId,
    viewer,
    reportViewRef,
    isPublicProject,
  });

  const {setCommentFrame} = useContext(ReportDiscussionUpdaterContext);
  const {commentsCount} = useContext(ReportDiscussionContext);

  const hasShareAccess =
    viewerOwnsReport ||
    viewerIsTeamMember ||
    !view.locked ||
    isAdmin ||
    isPublicProject;

  return (
    <ConditionalWrapper condition={!isFloatingBar}>
      {hasShareAccess && (
        <ShareReportTrigger
          viewRef={reportViewRef}
          view={view}
          userIsMemberOfTeam={viewerIsTeamMember}
          userIsTeamAdmin={viewerIsTeamAdmin}
          shouldShowInviteTab={!!viewer}>
          <Button
            data-test="share-report-button"
            icon="share-export"
            variant={shareReportVariant ? shareReportVariant : 'primary'}>
            {'Share'}
          </Button>
        </ShareReportTrigger>
      )}
      <LoginPopup
        isLoggedIn={isLoggedIn}
        sideOffset={loginPopupOffset ?? 8}
        align="end"
        alignOffset={-120}>
        <Tooltip title="Add a comment">
          {/* Note: Button needs to be wrapped in <span> for tooltip to appear */}
          <span>
            <Button
              data-test="comment-report-button"
              icon="forum-chat-bubble"
              variant="ghost"
              onClick={() => {
                if (isLoggedIn) {
                  setCommentFrame('editor');
                }
              }}>
              {commentsCount === 0
                ? 'Comment'
                : `${commentsCount} comment${commentsCount > 1 ? 's' : ''}`}
            </Button>
          </span>
        </Tooltip>
      </LoginPopup>
      <LoginPopup
        isLoggedIn={isLoggedIn}
        sideOffset={loginPopupOffset ?? 8}
        align="end"
        alignOffset={-40}>
        <Tooltip title="Star this report">
          <span>
            <Button
              data-test="star-report-button"
              icon={starred ? 'star-filled' : 'star'}
              variant="ghost"
              onClick={() => {
                if (isLoggedIn) {
                  toggleStar();
                }
              }}>
              {!starCount // if starCount is 0, null, or undefined
                ? 'Star'
                : `${starCount} star${starCount > 1 ? 's' : ''}`}
            </Button>
          </span>
        </Tooltip>
      </LoginPopup>
      {!isFloatingBar && (
        <ReportPageMenuActions viewerOwnsReport={viewerOwnsReport} />
      )}
    </ConditionalWrapper>
  );
};
